'use client'

import { forwardRef, useState, useEffect, useRef, useCallback } from 'react'
import { DollarSign, AlertCircle, CheckCircle2, ArrowRightLeft, RotateCcw } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Currency Input Component
 * 
 * Enhanced currency input for Paraguayan financial systems:
 * - Support for Guaraníes (₲) and USD ($)
 * - Automatic formatting with thousands separators
 * - Real-time currency conversion
 * - Spanish-first validation and error messages
 * - Min/max validation for financial controls
 * - Auto-save functionality for forms
 * - Undo functionality for large amount changes
 * - WCAG AA+ accessibility compliance
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

// Currency configuration for Paraguay
const CURRENCIES = {
  PYG: {
    symbol: '₲',
    name: 'Guaraníes',
    nameEs: 'Guaraníes',
    nameEn: 'Paraguayan Guaraní',
    decimals: 0, // Guaraníes typically don't use decimals
    thousandsSeparator: '.',
    decimalSeparator: ',',
    symbolPosition: 'before' as const
  },
  USD: {
    symbol: '$',
    name: 'Dólares',
    nameEs: 'Dólares',
    nameEn: 'US Dollars',
    decimals: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.',
    symbolPosition: 'before' as const
  }
} as const

type CurrencyCode = keyof typeof CURRENCIES

interface UndoState {
  previousValue: number | null
  previousCurrency: CurrencyCode
  timeout: NodeJS.Timeout | null
  isActive: boolean
}

export interface BirhausCurrencyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'defaultValue' | 'size' | 'autoSave'> {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // BIRHAUS Principle #2: Honest data
  error?: string
  errorEs?: string
  errorEn?: string
  
  // Currency specific
  value?: number | null
  defaultValue?: number | null
  onChange?: (value: number | null, currency: CurrencyCode) => void
  
  // Currency configuration
  currency?: CurrencyCode
  defaultCurrency?: CurrencyCode
  allowCurrencySwitch?: boolean
  showCurrencyName?: boolean
  
  // Conversion (for IPU Admin integration)
  exchangeRate?: number // USD to PYG rate
  showConversion?: boolean
  onCurrencyChange?: (currency: CurrencyCode) => void
  
  // Validation
  minValue?: number
  maxValue?: number
  requiredMessage?: string
  requiredMessageEs?: string
  requiredMessageEn?: string
  
  // BIRHAUS Principle #8: Performance is UX
  autoSave?: boolean
  autoSaveDelay?: number
  onAutoSave?: (value: number | null, currency: CurrencyCode) => Promise<void>
  
  // BIRHAUS Principle #1: Form serves flow
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'filled' | 'minimal'
  
  // BIRHAUS Principle #5: Undo over confirm
  enableUndo?: boolean
  undoTimeoutMs?: number
  undoThreshold?: number // Minimum amount change to trigger undo
  
  // BIRHAUS Principle #6: Accessibility = dignity
  helpText?: string
  helpTextEs?: string
  helpTextEn?: string
  
  // Status indicators
  loading?: boolean
  success?: boolean
  
  // Cognitive load tracking
  fieldPriority?: 'required' | 'optional' | 'progressive'
  
  // Locale
  locale?: 'es' | 'en'
}

export const BirhausCurrencyInput = forwardRef<HTMLInputElement, BirhausCurrencyInputProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Description
  description,
  descriptionEs,
  descriptionEn,
  
  // Validation
  error,
  errorEs,
  errorEn,
  
  // Currency values
  value,
  defaultValue,
  onChange,
  
  // Currency configuration
  currency = 'PYG',
  defaultCurrency = 'PYG',
  allowCurrencySwitch = true,
  showCurrencyName = true,
  
  // Conversion
  exchangeRate = 7300, // Default USD to PYG rate (approximate)
  showConversion = true,
  onCurrencyChange,
  
  // Validation
  minValue,
  maxValue,
  requiredMessage,
  requiredMessageEs,
  requiredMessageEn,
  
  // Auto-save
  autoSave = false,
  autoSaveDelay = 2000,
  onAutoSave,
  
  // Appearance
  size = 'md',
  variant = 'default',
  
  // Undo functionality
  enableUndo = true,
  undoTimeoutMs = 10000,
  undoThreshold = 100000, // 100k PYG or 15 USD threshold
  
  // Accessibility
  helpText,
  helpTextEs,
  helpTextEn,
  
  // Status
  loading = false,
  success = false,
  
  // Cognitive load
  fieldPriority = 'optional',
  
  // Locale
  locale = 'es',
  
  // Standard props
  disabled = false,
  required = false,
  placeholder,
  ...props
}, ref) => {
  // State management
  const [currentCurrency, setCurrentCurrency] = useState<CurrencyCode>(currency || defaultCurrency)
  const [numericValue, setNumericValue] = useState<number | null>(value || defaultValue || null)
  const [displayValue, setDisplayValue] = useState<string>('')
  const [isFocused, setIsFocused] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [validationError, setValidationError] = useState<string>('')
  const [undoState, setUndoState] = useState<UndoState>({
    previousValue: null,
    previousCurrency: currentCurrency,
    timeout: null,
    isActive: false
  })
  
  // Refs
  const inputRef = useRef<HTMLInputElement>(null)
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout>()
  
  // Get current currency config
  const currencyConfig = safeAccess(CURRENCIES, currentCurrency, CURRENCIES.PYG)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, '')
  const displayDescription = getDisplayText(description, descriptionEs, descriptionEn, '')
  const displayError = error || validationError
  const displayHelpText = getDisplayText(helpText, helpTextEs, helpTextEn, '')
  const displayRequiredMessage = getDisplayText(requiredMessage, requiredMessageEs, requiredMessageEn, 'Este campo es requerido')
  
  // Format number according to currency
  const formatCurrency = useCallback((amount: number | null, currencyCode: CurrencyCode): string => {
    if (amount === null || amount === undefined) return ''
    
    const config = safeAccess(CURRENCIES, currencyCode, CURRENCIES.PYG)
    const formattedNumber = new Intl.NumberFormat('es-PY', {
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals,
      useGrouping: true
    }).format(amount)
    
    return `${config.symbol} ${formattedNumber}`
  }, [])
  
  // Parse input value to number
  const parseInput = useCallback((input: string): number | null => {
    if (!input || input.trim() === '') return null
    
    // Remove currency symbol and clean input
    const cleaned = input
      .replace(currencyConfig.symbol, '')
      .replace(/\s/g, '')
      .replace(/[.,]/g, (match, offset, string) => {
        // Determine if this is a decimal separator based on position
        const afterMatch = string.slice(offset + 1)
        const beforeMatch = string.slice(0, offset)
        
        // If there are exactly 2 digits after and it's the last occurrence, it's decimal
        if (afterMatch.length === 2 && !afterMatch.includes('.') && !afterMatch.includes(',')) {
          return '.'
        }
        // Otherwise it's a thousands separator
        return ''
      })
    
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? null : parsed
  }, [currencyConfig.symbol])
  
  // Validate amount
  const validateAmount = useCallback((amount: number | null): string => {
    if (required && (amount === null || amount === undefined)) {
      return displayRequiredMessage
    }
    
    if (amount !== null) {
      if (minValue !== undefined && amount < minValue) {
        return `El monto mínimo es ${formatCurrency(minValue, currentCurrency)}`
      }
      
      if (maxValue !== undefined && amount > maxValue) {
        return `El monto máximo es ${formatCurrency(maxValue, currentCurrency)}`
      }
    }
    
    return ''
  }, [required, minValue, maxValue, displayRequiredMessage, formatCurrency, currentCurrency])
  
  // Convert between currencies
  const convertCurrency = useCallback((amount: number, fromCurrency: CurrencyCode, toCurrency: CurrencyCode): number => {
    if (fromCurrency === toCurrency) return amount
    
    if (fromCurrency === 'USD' && toCurrency === 'PYG') {
      return amount * exchangeRate
    }
    
    if (fromCurrency === 'PYG' && toCurrency === 'USD') {
      return amount / exchangeRate
    }
    
    return amount
  }, [exchangeRate])
  
  // Handle undo functionality
  const handleUndo = useCallback(() => {
    if (undoState.isActive && undoState.timeout) {
      clearTimeout(undoState.timeout)
      setNumericValue(undoState.previousValue)
      setCurrentCurrency(undoState.previousCurrency)
      onChange?.(undoState.previousValue, undoState.previousCurrency)
      setUndoState({ previousValue: null, previousCurrency: currentCurrency, timeout: null, isActive: false })
    }
  }, [undoState, onChange, currentCurrency])
  
  // Setup undo for significant changes
  const setupUndo = useCallback((newValue: number | null, oldValue: number | null, newCurrency: CurrencyCode) => {
    if (!enableUndo) return
    
    const difference = Math.abs((newValue || 0) - (oldValue || 0))
    const threshold = currentCurrency === 'USD' ? undoThreshold / exchangeRate : undoThreshold
    
    if (difference >= threshold) {
      // Clear existing timeout
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
      
      // Set up new undo state
      const timeout = setTimeout(() => {
        setUndoState(prev => ({ ...prev, isActive: false, timeout: null }))
      }, undoTimeoutMs)
      
      setUndoState({
        previousValue: oldValue,
        previousCurrency: currentCurrency,
        timeout,
        isActive: true
      })
    }
  }, [enableUndo, undoThreshold, undoTimeoutMs, undoState.timeout, currentCurrency, exchangeRate])
  
  // Handle input change
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    setDisplayValue(inputValue)
    
    const parsed = parseInput(inputValue)
    const oldValue = numericValue
    
    setNumericValue(parsed)
    
    // Setup undo for significant changes
    setupUndo(parsed, oldValue, currentCurrency)
    
    // Validation
    const errorMessage = validateAmount(parsed)
    setValidationError(errorMessage)
    
    // Auto-save setup
    if (autoSave && onAutoSave && parsed !== null) {
      setAutoSaveStatus('idle')
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
      
      autoSaveTimeoutRef.current = setTimeout(async () => {
        setAutoSaveStatus('saving')
        try {
          await onAutoSave(parsed, currentCurrency)
          setAutoSaveStatus('saved')
          setTimeout(() => setAutoSaveStatus('idle'), 2000)
        } catch {
          setAutoSaveStatus('error')
        }
      }, autoSaveDelay)
    }
    
    // Call parent onChange
    onChange?.(parsed, currentCurrency)
  }, [parseInput, numericValue, setupUndo, currentCurrency, validateAmount, autoSave, onAutoSave, autoSaveDelay, onChange])
  
  // Handle currency switch
  const handleCurrencySwitch = useCallback(() => {
    if (!allowCurrencySwitch) return
    
    const newCurrency: CurrencyCode = currentCurrency === 'PYG' ? 'USD' : 'PYG'
    const oldValue = numericValue
    
    // Convert current value to new currency
    let convertedValue = oldValue
    if (oldValue !== null) {
      convertedValue = convertCurrency(oldValue, currentCurrency, newCurrency)
      setNumericValue(convertedValue)
    }
    
    setCurrentCurrency(newCurrency)
    onCurrencyChange?.(newCurrency)
    onChange?.(convertedValue, newCurrency)
    
    // Update display value
    if (convertedValue !== null) {
      setDisplayValue(formatCurrency(convertedValue, newCurrency))
    }
  }, [allowCurrencySwitch, currentCurrency, numericValue, convertCurrency, onCurrencyChange, onChange, formatCurrency])
  
  // Handle focus
  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    
    // Show raw number when focused (easier editing)
    if (numericValue !== null) {
      setDisplayValue(numericValue.toString())
    }
  }, [numericValue])
  
  // Handle blur
  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    
    // Format display value when blurred
    if (numericValue !== null) {
      setDisplayValue(formatCurrency(numericValue, currentCurrency))
    }
    
    // Final validation
    const errorMessage = validateAmount(numericValue)
    setValidationError(errorMessage)
  }, [numericValue, formatCurrency, currentCurrency, validateAmount])
  
  // Effect: Update display value when value changes externally
  useEffect(() => {
    if (value !== undefined && value !== numericValue) {
      setNumericValue(value)
      if (!isFocused) {
        setDisplayValue(value !== null ? formatCurrency(value, currentCurrency) : '')
      }
    }
  }, [value, numericValue, isFocused, formatCurrency, currentCurrency])
  
  // Effect: Update currency when currency prop changes
  useEffect(() => {
    if (currency && currency !== currentCurrency) {
      setCurrentCurrency(currency)
    }
  }, [currency, currentCurrency])
  
  // Effect: Initial display value setup
  useEffect(() => {
    if (numericValue !== null && !isFocused) {
      setDisplayValue(formatCurrency(numericValue, currentCurrency))
    }
  }, [numericValue, currentCurrency, formatCurrency, isFocused])
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [undoState.timeout])
  
  // Style classes
  const sizeClasses = {
    sm: 'text-sm px-2 py-1',
    md: 'text-base px-3 py-2',
    lg: 'text-lg px-4 py-3'
  }
  
  const variantClasses = {
    default: 'bg-white border border-gray-300',
    filled: 'bg-gray-50 border border-gray-200',
    minimal: 'bg-transparent border-b border-gray-300'
  }
  
  // Status colors
  const getStatusColor = () => {
    if (displayError) return 'border-red-300 focus:border-red-500'
    if (success) return 'border-green-300 focus:border-green-500'
    if (isFocused) return 'border-primary-500'
    return 'border-gray-300'
  }
  
  // Calculate conversion for display
  const conversionAmount = numericValue !== null && showConversion ? 
    convertCurrency(numericValue, currentCurrency, currentCurrency === 'PYG' ? 'USD' : 'PYG') : null
  
  return (
    <div className={cn('birhaus-currency-input', disabled && 'opacity-50')}>
      {/* Label */}
      {displayLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {displayLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
          {fieldPriority === 'optional' && (
            <span className="text-gray-400 ml-1 text-xs">(opcional)</span>
          )}
        </label>
      )}
      
      {/* Description */}
      {displayDescription && (
        <p className="text-sm text-gray-600 mb-2">
          {displayDescription}
        </p>
      )}
      
      {/* Input Container */}
      <div className="relative">
        <div className={cn(
          'flex items-center rounded-md transition-colors duration-200',
          'focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-opacity-50',
          safeAccess(variantClasses, variant, variantClasses.default),
          getStatusColor(),
          disabled && 'cursor-not-allowed bg-gray-50'
        )}>
          {/* Currency Symbol */}
          <div className="flex items-center px-3 py-2 text-gray-600 border-r border-gray-300">
            <span className="font-medium">{currencyConfig.symbol}</span>
            {showCurrencyName && (
              <span className="ml-1 text-xs text-gray-500">
                {currencyConfig.nameEs}
              </span>
            )}
          </div>
          
          {/* Amount Input */}
          <input
            ref={ref}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled || loading}
            required={required}
            placeholder={placeholder || `0${currencyConfig.decimals > 0 ? '.00' : ''}`}
            className={cn(
              'flex-1 border-none bg-transparent outline-none',
              safeAccess(sizeClasses, size, sizeClasses.md),
              'text-right font-mono',
              disabled && 'cursor-not-allowed'
            )}
            {...props}
          />
          
          {/* Currency Switch Button */}
          {allowCurrencySwitch && (
            <button
              type="button"
              onClick={handleCurrencySwitch}
              disabled={disabled}
              className="px-2 py-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Cambiar moneda"
            >
              <ArrowRightLeft className="h-4 w-4" />
            </button>
          )}
          
          {/* Status Icons */}
          <div className="flex items-center px-2">
            {loading && (
              <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-primary-600 rounded-full" />
            )}
            {success && <CheckCircle2 className="h-4 w-4 text-green-500" />}
            {displayError && <AlertCircle className="h-4 w-4 text-red-500" />}
          </div>
        </div>
      </div>
      
      {/* Conversion Display */}
      {showConversion && conversionAmount !== null && !isFocused && (
        <div className="mt-1 text-sm text-gray-600">
          ≈ {formatCurrency(conversionAmount, currentCurrency === 'PYG' ? 'USD' : 'PYG')}
        </div>
      )}
      
      {/* Auto-save Status */}
      {autoSave && autoSaveStatus !== 'idle' && (
        <div className="mt-1 text-xs">
          {autoSaveStatus === 'saving' && <span className="text-gray-500">Guardando...</span>}
          {autoSaveStatus === 'saved' && <span className="text-green-600">Guardado</span>}
          {autoSaveStatus === 'error' && <span className="text-red-600">Error al guardar</span>}
        </div>
      )}
      
      {/* Error Message */}
      {displayError && (
        <div className="mt-1 text-sm text-red-600 flex items-start space-x-1">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{displayError}</span>
        </div>
      )}
      
      {/* Help Text */}
      {!displayError && displayHelpText && (
        <div className="mt-1 text-sm text-gray-600">
          {displayHelpText}
        </div>
      )}
      
      {/* Undo Notification */}
      {undoState.isActive && (
        <div className="mt-2 bg-gray-800 text-white text-sm rounded-md p-3 flex items-center justify-between">
          <span>
            Monto cambiado a {formatCurrency(numericValue, currentCurrency)}
          </span>
          <button
            type="button"
            onClick={handleUndo}
            className="flex items-center space-x-1 text-primary-200 hover:text-primary-100 font-medium"
          >
            <RotateCcw className="h-3 w-3" />
            <span>Deshacer</span>
          </button>
        </div>
      )}
    </div>
  )
})

BirhausCurrencyInput.displayName = 'BirhausCurrencyInput'

// Hook for currency management
export const useBirhausCurrency = (initialValue: number | null = null, initialCurrency: CurrencyCode = 'PYG') => {
  const [amount, setAmount] = useState<number | null>(initialValue)
  const [currency, setCurrency] = useState<CurrencyCode>(initialCurrency)
  const [exchangeRate, setExchangeRate] = useState<number>(7300) // Default rate
  
  const convert = useCallback((value: number, fromCurrency: CurrencyCode, toCurrency: CurrencyCode): number => {
    if (fromCurrency === toCurrency) return value
    
    if (fromCurrency === 'USD' && toCurrency === 'PYG') {
      return value * exchangeRate
    }
    
    if (fromCurrency === 'PYG' && toCurrency === 'USD') {
      return value / exchangeRate
    }
    
    return value
  }, [exchangeRate])
  
  const getFormattedAmount = useCallback((currencyCode?: CurrencyCode): string => {
    if (amount === null) return ''
    const targetCurrency = currencyCode || currency
    const config = safeAccess(CURRENCIES, targetCurrency, CURRENCIES.PYG)
    
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: targetCurrency === 'PYG' ? 'PYG' : 'USD',
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals
    }).format(amount)
  }, [amount, currency])
  
  const updateAmount = useCallback((newAmount: number | null, newCurrency?: CurrencyCode) => {
    setAmount(newAmount)
    if (newCurrency) {
      setCurrency(newCurrency)
    }
  }, [])
  
  return {
    amount,
    currency,
    exchangeRate,
    setExchangeRate,
    convert,
    getFormattedAmount,
    updateAmount,
    amountInPYG: amount !== null ? convert(amount, currency, 'PYG') : null,
    amountInUSD: amount !== null ? convert(amount, currency, 'USD') : null
  }
}

// Utility functions for financial calculations
export const paraguayanCurrencyUtils = {
  // Format number as Guaraníes
  formatPYG: (amount: number): string => {
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  },
  
  // Format number as USD
  formatUSD: (amount: number): string => {
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  },
  
  // Convert USD to PYG
  usdToPyg: (usdAmount: number, rate: number = 7300): number => {
    return Math.round(usdAmount * rate)
  },
  
  // Convert PYG to USD
  pygToUsd: (pygAmount: number, rate: number = 7300): number => {
    return pygAmount / rate
  },
  
  // Validate Paraguayan tax ID (RUC)
  validateRUC: (ruc: string): boolean => {
    const cleaned = ruc.replace(/\D/g, '')
    return cleaned.length >= 6 && cleaned.length <= 8
  },
  
  // Calculate IVA (VAT) for Paraguay (10%)
  calculateIVA: (amount: number): { baseAmount: number; ivaAmount: number; totalAmount: number } => {
    const baseAmount = amount / 1.1
    const ivaAmount = amount - baseAmount
    return {
      baseAmount: Math.round(baseAmount),
      ivaAmount: Math.round(ivaAmount),
      totalAmount: Math.round(amount)
    }
  }
}